import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../../../components/button';
import InputText from '../../../components/input-text/inputText';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import OtpForm from '../../../components/otp-form';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { useWealthlaneCustomerApi } from '../../../hooks';
import { ParticipantSignUpService } from '../../../services/wealthlane-customer-services';
import {
  STEPTWO_VALIDATION,
  COMMUNICATION_EMAIL_VALIDATION,
} from '../../../utils/validations/validations';
import CustomerRadio from '../../../components/radio-button/customRadio';
import { ContentBody, ContentContainer, ContentHeader } from './signUpSteps.styled';
import { GoBackBlock } from '../../../constants/style/common.styled';

type Phase =
  | 'recovery_form'
  | 'recovery_otp'
  | 'use_as_communication_question'
  | 'communication_form'
  | 'communication_otp';

interface StepTwoType {
  setStep: (value: number) => void;
}

const StepTwo = ({ setStep }: StepTwoType): JSX.Element => {
  const { handleRequest, isLoading, error } = useWealthlaneCustomerApi();

  const stepTwoValues = window.sessionStorage.getItem('step2');

  const [phase, setPhase] = useState<Phase>('recovery_form');
  const [formData, setFormData] = useState<any>({});
  const [defaultError, setDefaultError] = useState('');
  const [useSameEmail, setUseSameEmail] = useState<boolean | null>(null);

  useEffect(() => {
    if (error?.body?.error?.validationErrors[0]?.message.includes('OTP'))
      setDefaultError(error?.body?.error?.validationErrors[0]?.message);
  }, [error]);

  const getBasePayload = () => {
    const token = window.sessionStorage.getItem('token');
    const formValues = window.sessionStorage.getItem('step1');
    const parsed = JSON.parse(formValues as any);
    return {
      socialSecurityNumber: parsed?.socialSecurityNumber,
      employeeId: parsed?.employeeId,
      dateOfBirth: parsed?.dateOfBirth,
      hireDate: parsed?.hireDate,
      companyTickerSymbol: parsed?.companyTickerSymbol,
      isUsCitizen: parsed?.isUsCitizen,
      token,
      code: parsed?.code,
    };
  };

  function handleRecoveryEmailSubmit(values: any): void {
    const basePayload = getBasePayload();
    const payload = {
      ...basePayload,
      email: values.recoveryEmail,
      recoveryEmail: values.recoveryEmail,
      emailType: 2,
    };

    handleRequest(
      ParticipantSignUpService.postApiCustomerApiAppParticipantSignUpParticipantEmailVerification(
        payload
      )
    )
      .then((response: any) => {
        if (response != null) {
          const step2 = {
            ...payload,
            ...response,
            ...values,
            code: payload?.code,
            token: payload?.token,
          };
          window.sessionStorage.setItem('step2', JSON.stringify(step2));
          setFormData({ ...payload });
          setPhase('recovery_otp');
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function handleCommunicationEmailSubmit(values: any): void {
    const basePayload = getBasePayload();
    const payload = {
      ...basePayload,
      email: values.communicationEmail,
      communicationEmail: values.communicationEmail,
      recoveryEmail: formData?.recoveryEmail,
      emailType: 1,
    };

    handleRequest(
      ParticipantSignUpService.postApiCustomerApiAppParticipantSignUpParticipantEmailVerification(
        payload
      )
    )
      .then((response: any) => {
        if (response != null) {
          const step2Data = JSON.parse(window.sessionStorage.getItem('step2') as any) ?? {};
          const updatedStep2 = {
            ...step2Data,
            ...payload,
            ...response,
            ...values,
            code: payload?.code,
            token: payload?.token,
          };
          window.sessionStorage.setItem('step2', JSON.stringify(updatedStep2));
          setFormData({ ...payload });
          setPhase('communication_otp');
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const resendOtp = (): void => {
    handleRequest(
      ParticipantSignUpService.postApiCustomerApiAppParticipantSignUpResendParticipantOTp({
        ...formData,
      } as any)
    )
      .then((response: any) => {
        if (response != null) {
          toast.success(<ToastComponent label='Success' message='OTP resend.' />);
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  };

  function handleRecoveryOtpSubmit(values: any): void {
    const payload = {
      ...formData,
      otp: values?.otp,
    };
    handleRequest(
      ParticipantSignUpService.postApiCustomerApiAppParticipantSignUpParticipantOTpVerification(
        payload as any
      )
    )
      .then((response: any) => {
        if (response != null) {
          const otpVerification = { ...response };
          window.sessionStorage.setItem('otpVerification', JSON.stringify(otpVerification));
          setPhase('use_as_communication_question');
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  function handleCommunicationOtpSubmit(values: any): void {
    const payload = {
      ...formData,
      otp: values?.otp,
    };
    handleRequest(
      ParticipantSignUpService.postApiCustomerApiAppParticipantSignUpParticipantOTpVerification(
        payload as any
      )
    )
      .then((response: any) => {
        if (response != null) {
          const otpVerification = { ...response };
          window.sessionStorage.setItem('otpVerification', JSON.stringify(otpVerification));
          setStep(3);
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  function handleUseSameEmailSubmit(): void {
    if (useSameEmail === true) {
      setStep(3);
    } else if (useSameEmail === false) {
      setPhase('communication_form');
    }
  }

  const renderRecoveryForm = (): JSX.Element => (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          ...(JSON.parse(stepTwoValues as any) ?? InitialValuesForm.SignUp.stepTwo),
        }}
        validationSchema={STEPTWO_VALIDATION}
        onSubmit={handleRecoveryEmailSubmit}>
        {({ handleChange, errors, touched }) => {
          return (
            <Form>
              <ModalBodyComponent>
                <div className='grid md:grid-cols-3'>
                  <div className=''>
                    <InputText
                      type='text'
                      label='Enter your Recovery Email'
                      name='recoveryEmail'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      required
                    />
                    <InputText
                      type='text'
                      label='Re-Enter your Recovery Email'
                      name='confirmRecoveryEmail'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      onPaste={(e: any) => e.preventDefault()}
                      required
                    />
                  </div>
                </div>
                <div className='mt-12 w-full flex'>
                  <Button
                    as='Create'
                    variant={'primary'}
                    title={'Request Authorization Code'}
                    type='submit'
                    loading={isLoading}
                    btnClassName='whitespace-normal sm:whitespace-nowrap'
                  />
                </div>
              </ModalBodyComponent>
            </Form>
          );
        }}
      </Formik>
    </div>
  );

  const renderUseSameEmailQuestion = (): JSX.Element => (
    <div>
      <Formik
        initialValues={{ useSameEmail: '' }}
        onSubmit={() => {
          handleUseSameEmailSubmit();
        }}>
        {({ setFieldValue }) => (
          <Form>
            <ModalBodyComponent>
              <Heading
                variant='h4'
                title={`Do you want to use Recovery email (${formData?.recoveryEmail}) as Communication email?`}
                className='mb-6'
              />
              <div className='flex gap-8 mb-8'>
                <CustomerRadio
                  name='useSameEmail'
                  id='useSameEmail_yes'
                  label='Yes'
                  value='yes'
                  checked={useSameEmail === true}
                  onChange={() => {
                    setUseSameEmail(true);
                    void setFieldValue('useSameEmail', 'yes');
                  }}
                />
                <CustomerRadio
                  name='useSameEmail'
                  id='useSameEmail_no'
                  label='No'
                  value='no'
                  checked={useSameEmail === false}
                  onChange={() => {
                    setUseSameEmail(false);
                    void setFieldValue('useSameEmail', 'no');
                  }}
                />
              </div>
              <div className='mt-4 w-full flex'>
                <Button
                  as='Create'
                  variant={'primary'}
                  title={'Continue'}
                  type='submit'
                  loading={isLoading}
                  disabled={useSameEmail === null}
                  btnClassName='whitespace-normal sm:whitespace-nowrap'
                />
              </div>
            </ModalBodyComponent>
          </Form>
        )}
      </Formik>
    </div>
  );

  const renderCommunicationForm = (): JSX.Element => (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          communicationEmail: '',
          confirmCommunicationEmail: '',
        }}
        validationSchema={COMMUNICATION_EMAIL_VALIDATION}
        onSubmit={handleCommunicationEmailSubmit}>
        {({ handleChange, errors, touched }) => {
          return (
            <Form>
              <ModalBodyComponent>
                <GoBackBlock
                  onClick={() => {
                    setPhase('use_as_communication_question');
                  }}
                  className='mb-8'>
                  <span className='material-symbols-outlined'>arrow_back</span>
                  <Heading className='goBack' variant='body-s' title='Go back' />
                </GoBackBlock>
                <div className='grid md:grid-cols-3 mt-4'>
                  <div className=''>
                    <InputText
                      type='text'
                      label='Enter Your Communication Email'
                      name='communicationEmail'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      required
                    />
                    <InputText
                      type='text'
                      label='Re-Enter Your Communication Email'
                      name='confirmCommunicationEmail'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      onPaste={(e: any) => e.preventDefault()}
                      required
                    />
                  </div>
                </div>
                <div className='mt-12 w-full flex'>
                  <Button
                    as='Create'
                    variant={'primary'}
                    title={'Request Authorization Code'}
                    type='submit'
                    loading={isLoading}
                    btnClassName='whitespace-normal sm:whitespace-nowrap'
                  />
                </div>
              </ModalBodyComponent>
            </Form>
          );
        }}
      </Formik>
    </div>
  );

  const renderPhaseContent = (): JSX.Element => {
    switch (phase) {
      case 'recovery_form':
        return renderRecoveryForm();
      case 'recovery_otp':
        return (
          <div>
            <OtpForm
              handleRequest={handleRequest}
              formData={formData}
              setStep={setStep}
              isLoading={isLoading}
              handleSubmit={handleRecoveryOtpSubmit}
              handleResendOtp={resendOtp}
              errorMessage={defaultError}
              setDefaultError={setDefaultError}
            />
          </div>
        );
      case 'use_as_communication_question':
        return renderUseSameEmailQuestion();
      case 'communication_form':
        return renderCommunicationForm();
      case 'communication_otp':
        return (
          <div>
            <OtpForm
              handleRequest={handleRequest}
              formData={formData}
              setStep={setStep}
              isLoading={isLoading}
              handleSubmit={handleCommunicationOtpSubmit}
              handleResendOtp={resendOtp}
              errorMessage={defaultError}
              setDefaultError={setDefaultError}
            />
          </div>
        );
      default:
        return renderRecoveryForm();
    }
  };

  return (
    <ContentContainer>
      <ContentHeader>
        <Heading variant={'h4'} title={'Verify Your Email'} className='title' />
      </ContentHeader>
      <ContentBody>{renderPhaseContent()}</ContentBody>
    </ContentContainer>
  );
};

export default StepTwo;
